import React, { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/pro-light-svg-icons";

import Badge from "ui/Badge";
import Button from "ui/Button";
import Grid, { GridColumn } from "ui/Grid";
import Panel from "ui/Panel";

import { useQueryStringSearch } from "ui/QueryStringSearch";
import { useDeleteWorkbookMutation } from "features/Workbooks/WorkbookList/WorkbookVariantSidebar/gql/Mutation.workbook.generated";
import { formatTimestampShort } from "utils/format";
import { makeFilter } from "utils/filter";
import { useRoutes } from "utils/routes";

export type WorkbookListRow = {
  id: string;
  name: string;
  draft: boolean;
  variantsCount: number;
  lastActivityAt: number;
  user?: { id: string; fullname?: string | null } | null;
  server: {
    id: string;
    humanId: string;
    name: string;
  };
  database: {
    id: string;
    datname?: string | null;
  };
  channel?: string | null;
};

type GridRow = WorkbookListRow & {
  serverName: string;
  databaseName: string;
};

type Props = {
  title?: string;
  workbooks: WorkbookListRow[];
  showServerColumn?: boolean;
  showDatabaseColumn?: boolean;
  onDeleted?: () => void;
  toolbar?: React.ReactNode;
};

const WorkbookListTable: React.FC<Props> = ({
  title = "All Workbooks",
  workbooks,
  showServerColumn = false,
  showDatabaseColumn = false,
  onDeleted,
  toolbar,
}) => {
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const [showDraft, setShowDraft] = useState(false);
  const { databaseWorkbookVariants } = useRoutes();
  const [deleteWorkbook] = useDeleteWorkbookMutation();

  const filteredData = workbooks
    .filter((w) => (showDraft ? true : !w.draft))
    .filter(makeFilter(searchTerm, "name"))
    .map((w) => ({
      ...w,
      serverName: w.server.name,
      databaseName: w.database.datname ?? w.database.id,
    }));

  const secondaryTitle = (
    <>
      <div className="flex">
        <input
          type="checkbox"
          checked={showDraft}
          id="show_draft"
          onChange={(e) => setShowDraft(e.target.checked)}
        />
        <label htmlFor="show_draft">Show draft workbooks</label>
      </div>
      <SearchInput />
    </>
  );

  const columns: GridColumn<GridRow, keyof GridRow>[] = [
    {
      field: "name",
      header: "Workbook",
      width: "1fr",
      renderer: function NameCell({
        rowData,
        fieldData,
      }: {
        rowData: GridRow;
        fieldData: string;
      }) {
        return (
          <div className="flex flex-col">
            <div className="flex items-center">
              <Link
                to={databaseWorkbookVariants(rowData.database.id, rowData.id)}
              >
                {fieldData}
              </Link>
              {rowData.draft && <Badge className="!ml-2">Draft</Badge>}
              {rowData.channel === "mcp" && (
                <Badge className="!ml-2" title="Created via the MCP server">
                  MCP
                </Badge>
              )}
            </div>
            <span>{rowData.user?.fullname ?? "Unknown User"}</span>
          </div>
        );
      },
    },
    {
      field: "serverName" as const,
      header: "Server",
      width: showServerColumn ? "180px" : false,
    },
    {
      field: "databaseName" as const,
      header: "Database",
      width: showDatabaseColumn ? "180px" : false,
    },
    {
      field: "variantsCount",
      header: "Variants",
      width: "120px",
      style: "number",
    },
    {
      field: "lastActivityAt",
      header: "Last Activity",
      width: "200px",
      defaultSortOrder: "DESC",
      className: "text-right",
      headerClassName: "text-right",
      renderer: function LastActivityCell({
        fieldData,
      }: {
        fieldData: number;
      }) {
        return formatTimestampShort(moment.unix(fieldData));
      },
    },
    {
      field: "id",
      header: "",
      width: "40px",
      renderer: function DeleteCell({ rowData }: { rowData: GridRow }) {
        return (
          <Button
            bare
            onClick={() => {
              if (
                window.confirm(
                  `Delete a draft workbook "${rowData.name}", created by ${rowData.user?.fullname}?`,
                )
              ) {
                deleteWorkbook({
                  variables: { workbookId: rowData.id },
                  onCompleted: onDeleted,
                });
              }
            }}
            title="Delete"
          >
            <FontAwesomeIcon icon={faTrashAlt} className="text-[#CA1515]" />
          </Button>
        );
      },
    },
  ];

  return (
    <>
      {toolbar && <div className="flex justify-end mb-2">{toolbar}</div>}
      <Panel title={title} secondaryTitle={secondaryTitle}>
        <Grid
          data={filteredData}
          defaultSortBy="lastActivityAt"
          pageSize={10}
          columns={columns}
        />
      </Panel>
    </>
  );
};

export default WorkbookListTable;
